import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StackButton from "@/components/buttons/StackButton";
import { FormPageNameField } from "@/components/forms/FormPageNameField";
import StatusToggle from "@/components/toggles/StatusToggle";
import { DEFAULT_FORM_TOGGLE_ROW_COUNT, ToggleListSkeleton } from "@/components/skeletons";
import { saveGroup } from '@/services/group.service';
import { getRolesListFromAPI } from "@/services/role.service";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_ROLES } from "@/utils/constants/constants";
import { GROUP_CREATED_SUCCESSFULLY } from '@/utils/constants/messages';
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { errorToasterMessage, successToasterMessage } from '@/utils/functions/toaster';


const CreateGroup = () => {
    const navigate = useNavigate();
    const [groupName, setGroupName] = React.useState("");
    const [allRoles, setAllRoles] = React.useState<any>([]);
    const [checkedCount, setCheckedCount] = React.useState(0);
    const [loader, setLoader] = React.useState(false);

    const handleSaveGroup = () => {
        const newRoleList = allRoles.filter((role: any) => role.checked);
        const newRoleIdList = newRoleList.map((role: any) => role.id);
        const data = JSON.stringify({
            name: groupName,
            roles: newRoleIdList,
        });
        setLoader(true);
        saveGroup(data, () => {
            setLoader(false);
            successToasterMessage(GROUP_CREATED_SUCCESSFULLY);
            navigate(-1);
        }, (error: any) => {
            setLoader(false);
            console.log(error);
            errorToasterMessage(error.data.message);
        });
    };

    useEffect(() => {
        getRolesListFromAPI()
            .then((response) => {
                storeInLocalStorage(ALL_ROLES, response.data.roles);
                const newRolesList = response.data.roles.map((role: any) => ({
                    ...role,
                    checked: false,
                }));
                setAllRoles(newRolesList);
            })
            .catch((e) => {
                console.log(e);
            });
    }, []);

    const onChangeRole = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newRoleList = allRoles.map((role: any) => {
            if (e.target.name === role.name) {
                const newRole = { ...role, checked: e.target.checked };
                setCheckedCount(checkedCount + (e.target.checked ? 1 : -1));
                return newRole;
            }
            return role;
        });
        setAllRoles(newRoleList);
    };

    const isSaveDisabled = checkedCount === 0 || !groupName.trim();

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title">
                    <strong id="create-group-title">{HEADINGS.CREATE_GROUP}</strong>
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="create-group-save-btn"
                    secondaryId="create-group-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleSaveGroup}
                    handleSecondary={() => navigate(-1)}
                    disabled={isSaveDisabled}
                    isLoading={loader}
                />
            </div>

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isSaveDisabled && !loader) {
                        handleSaveGroup();
                    }
                }}
            >
                <FormPageNameField
                    label={LABELS.GROUP_NAME}
                    labelId="create-group-name-label"
                    inputId="create-group-name-input"
                    autoFocus
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                />

                <div className="gs-form-page-section">
                    <h5 className="gs-form-section-title gs-form-section-title--spaced" id="create-group-role-title">
                        {HEADINGS.ROLES}
                    </h5>
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list gs-form-toggle-list">
                            {allRoles.length > 0 ? (
                                allRoles.map((role: any, key: number) => (
                                    <li className="gs-detail-list-item" key={role.id}>
                                        <StatusToggle
                                            index={key}
                                            onChange={onChangeRole}
                                            name={role.name as string}
                                            id={role.id as string}
                                            checked={role.checked}
                                            disabled={role.name === "superadmin"}
                                        >
                                            <p className="m-0" id={`create-group-role-name-${key}`}>{role.name}</p>
                                        </StatusToggle>
                                    </li>
                                ))
                            ) : (
                                <ToggleListSkeleton rowCount={DEFAULT_FORM_TOGGLE_ROW_COUNT} />
                            )}
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateGroup;
